import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type PropsWithChildren
} from "react";
import type { WaMessageSetup } from "features/waMessaging/types";

export type WaMessageSetupMode = 'view' | 'edit' | 'create';

type WaMessageSetupContextValue = {
    readonly open: boolean;
    readonly mode: WaMessageSetupMode | null;
    readonly resource: WaMessageSetup | undefined;
    readonly openView: (resource: WaMessageSetup) => void;
    readonly openEdit: (resource: WaMessageSetup) => void;
    readonly openCreate: () => void;
    readonly onClose: () => void;
};

const WaMessageSetupContext = createContext<WaMessageSetupContextValue>(
    {} as WaMessageSetupContextValue
);

const WaMessageSetupProvider = ({ children }: PropsWithChildren) => {
    const [mode, setMode] = useState<WaMessageSetupMode | null>(null);
    const [resource, setResource] = useState<WaMessageSetup | undefined>();
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const openView = useCallback((next: WaMessageSetup) => {
        clearTimeout(timeoutRef.current);
        setResource(next);
        setMode('view');
    }, []);

    const openEdit = useCallback((next: WaMessageSetup) => {
        clearTimeout(timeoutRef.current);
        setResource(next);
        setMode('edit');
    }, []);

    const openCreate = useCallback(() => {
        clearTimeout(timeoutRef.current);
        setResource(undefined);
        setMode('create');
    }, []);

    const onClose = useCallback(() => {
        setMode(null);
        timeoutRef.current = setTimeout(() => setResource(undefined), 300);
    }, []);

    const value = useMemo<WaMessageSetupContextValue>(() => ({
        open: mode !== null,
        mode,
        resource,
        openView,
        openEdit,
        openCreate,
        onClose
    }), [mode, resource, openView, openEdit, openCreate, onClose]);

    return (
        <WaMessageSetupContext.Provider value={value}>
            {children}
        </WaMessageSetupContext.Provider>
    );
};

export const useWaMessageSetupContext = () => useContext(WaMessageSetupContext);

export default WaMessageSetupProvider;
